/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import FetchStatus from 'src/types/FetchStatus';
import { customFetch, constructQueryParams } from 'src/utils';
import { CategoriesResponse, Category, Tag } from 'src/types/Categories';
import { GET_CATEGORIES_API } from 'src/constants/apiEndpoints';
import { getI18n } from 'react-i18next';
import READ_ONLY_CATEGORIES_NAME_ID from 'src/constants/categories';
import type { AppState } from '../store';
import { selectMarketplaceId } from './userInfo';

export interface CreateThreadState {
  categories: { [key: string]: Category & { tags: Array<string> } },
  tags: { [key: string]: Tag },
  status: FetchStatus,
  error?: string,
  categoriesCountMap?: Record<string, number>
}

export const initialState: CreateThreadState = {
  categories: {},
  tags: {},
  status: FetchStatus.idle,
};

export const fetchCategories = createAsyncThunk(
  'threadCategories/fetchCategories',
  async (noop, { rejectWithValue, getState }) => {
    const apiEndpoint = constructQueryParams({
      marketplaceId: selectMarketplaceId(getState() as AppState),
    }, GET_CATEGORIES_API);
    const categories = await customFetch(
      apiEndpoint,
      undefined,
      (error: Error) => rejectWithValue(error.message),
      1,
    ) as CategoriesResponse;
    return categories;
  },
);

const threadCategories = createSlice({
  name: 'threadCategories',
  initialState,
  reducers: {
    setCategoriesState: (
      state: CreateThreadState,
      action: PayloadAction<CategoriesResponse>,
    ) => {
      state.status = FetchStatus.complete;

      const tags: CreateThreadState['tags'] = action.payload.categories.reduce((acc, category) => {
        const categoryTags = category.tags.reduce((tagsAcc, tag) => ({
          ...tagsAcc,
          [tag.id]: tag,
        }), {});
        return {
          ...acc,
          ...categoryTags,
        };
      }, {});

      const categories: CreateThreadState['categories'] = action.payload.categories.reduce((acc, category) => ({
        ...acc,
        [category.id]: {
          ...category,
          readOnly: READ_ONLY_CATEGORIES_NAME_ID.includes(category.nameStringId),
          tags: category.tags.map((tag) => tag.id).filter((tagId) => !!tags[tagId]),
        },
      }), {});

      state.categories = categories;
      state.tags = tags;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.rejected, (state: CreateThreadState, action) => {
        state.error = action.payload as string;
        state.status = FetchStatus.failed;
      })
      .addCase(fetchCategories.pending, (state: CreateThreadState) => {
        state.status = FetchStatus.loading;
      })
      .addCase(fetchCategories.fulfilled, (state: CreateThreadState, action) => {
        state.status = FetchStatus.complete;
        const i18n = getI18n();

        const tags: CreateThreadState['tags'] = action.payload.categories.reduce((acc, category) => {
          const categoryTags = category.tags.reduce((tagsAcc, tag) => {
            // Filter out tags that don't have translations
            if (!i18n.exists(tag.stringId)) {
              return tagsAcc;
            }
            return ({
              ...tagsAcc,
              [tag.id]: tag,
            });
          }, {});
          return {
            ...acc,
            ...categoryTags,
          };
        }, {});

        const categories: CreateThreadState['categories'] = action.payload.categories.reduce((acc, category) => {
          // Filter out categories that don't have translations
          if (!i18n.exists(category.nameStringId)) {
            return acc;
          }
          return ({
            ...acc,
            [category.id]: {
              ...category,
              readOnly: READ_ONLY_CATEGORIES_NAME_ID.includes(category.nameStringId),
              tags: category.tags.map((tag) => tag.id).filter((tagId) => !!tags[tagId]),
            },
          });
        }, {});

        state.categories = categories;
        state.tags = tags;
      });
  },
});

export const threadCategoriesActions = threadCategories.actions;

export default threadCategories.reducer;

export const selectSetPreferenceCategories = (state: AppState) => Object.values(
  state.threadCategories.categories,
).filter((category) => !category.readOnly);
